import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IDisplayField, IPVTCalculator, PVTCriteriaOption, PVTStrategyOption, unitId } from "../../interfaces/interfaces";
import { apiSlice } from "./apiSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";

interface IRequestPlot {
	pressure: Record<string, number>,
	z: Record<string, number>,
	m: Record<string, number>,
}

interface IPVTRequestResult {
	results: Record<string, number>,
	plots: Record<string, IRequestPlot>,
	warnings: [],
	errors: []
}

const converter = new UnitConverter();

const initialState: IPVTCalculator = {
	inputs: [
		{
			id: 'pvt-field-pressure',
			type: 'string',
			label: '',
			queryName: 'pressure',
			baseUnitId: 'bar',
			baseValue: 360.00,
			currentValue: 360.00,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
			min: 1.01325,
			max: 500
		},
		{
			id: 'pvt-field-temperature',
			type: 'string',
			label: '',
			queryName: 'temperature',
			baseUnitId: "celsius",
			baseValue: 87.00,
			currentValue: 87.00,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
			min: -273.15,
		},
	],
	gInputs: [
		{
			id: 'pvt-field-specific-gravity',
			type: 'string',
			label: '',
			queryName: 'gas_density',
			baseUnitId: null,
			baseValue: 0.63,
			currentValue: 0.63,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pvt-field-xCO2',
			type: 'string',
			label: '',
			queryName: 'x_co2',
			baseUnitId: null,
			baseValue: 0.00359,
			currentValue: 0.00359,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pvt-field-xH2S',
			type: 'string',
			label: '',
			queryName: 'x_h2s',
			baseUnitId: null,
			baseValue: 0.0001,
			currentValue: 0.0001,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pvt-field-xN2',
			type: 'string',
			label: '',
			queryName: 'x_n2',
			baseUnitId: null,
			baseValue: 0.00783,
			currentValue: 0.00783,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
	],
	gasCompositionInputs: [
		{
			id: 'pvt-field-c1',
			type: 'string',
			label: '',
			queryName: 'c1',
			baseUnitId: null,
			baseValue: 0.86959,
			currentValue: 0.86959,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0.6,
		},
		{
			id: 'pvt-field-c2',
			type: 'string',
			label: '',
			queryName: 'c2',
			baseUnitId: null,
			baseValue: 0.05711,
			currentValue: 0.05711,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-c3',
			type: 'string',
			label: '',
			queryName: 'c3',
			baseUnitId: null,
			baseValue: 0.02457,
			currentValue: 0.02457,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-ic4',
			type: 'string',
			label: '',
			queryName: 'ic4',
			baseUnitId: null,
			baseValue: 0.00543,
			currentValue: 0.00543,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-nc4',
			type: 'string',
			label: '',
			queryName: 'nc4',
			baseUnitId: null,
			baseValue: 0.00715,
			currentValue: 0.00715,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-ic5',
			type: 'string',
			label: '',
			queryName: 'ic5',
			baseUnitId: null,
			baseValue: 0.00266,
			currentValue: 0.00266,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-nc5',
			type: 'string',
			label: '',
			queryName: 'nc5',
			baseUnitId: null,
			baseValue: 0.00243,
			currentValue: 0.00243,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-c6',
			type: 'string',
			label: '',
			queryName: 'c6',
			baseUnitId: null,
			baseValue: 0.00277,
			currentValue: 0.00277,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-c7',
			type: 'string',
			label: '',
			queryName: 'c7',
			baseUnitId: null,
			baseValue: 0.011809,
			currentValue: 0.011809,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-c8',
			type: 'string',
			label: '',
			queryName: 'c8',
			baseUnitId: null,
			baseValue: 0.005061,
			currentValue: 0.005061,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-n2',
			type: 'string',
			label: '',
			queryName: 'n2',
			baseUnitId: null,
			baseValue: 0.00783,
			currentValue: 0.00783,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-co2',
			type: 'string',
			label: '',
			queryName: 'co2',
			baseUnitId: null,
			baseValue: 0.00359,
			currentValue: 0.00359,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'pvt-field-h2s',
			type: 'string',
			label: '',
			queryName: 'h2s',
			baseUnitId: null,
			baseValue: 0,
			currentValue: 0,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
	],
	criteria: {
		id: "pvt-dd-criteria",
		selected: "bb",
		options: ["bb", "hy", "dak"],
	},
	strategy: {
		id: "pvt-dd-strategy",
		selected: "gascompose",
		options: ["g", "gascompose"],
	},
	results: [
		{
			id: 'pvt-result-z',
			type: 'string',
			label: '',
			value: null,
			queryName: 'z'
		},
		{
			id: 'pvt-result-b',
			type: 'string',
			label: '',
			value: null,
			queryName: 'b'
		},
		{
			id: 'pvt-result-p',
			type: 'string',
			label: '',
			value: null,
			queryName: 'p'
		},
		{
			id: 'pvt-result-m',
			type: 'string',
			label: '',
			value: null,
			queryName: 'm'
		}
	],
	intermediateResults: [
		{
			id: 'pvt-mwa',
			type: 'string',
			label: '',
			value: null,
			queryName: 'mwa'
		},
		{
			id: 'pvt-ppc',
			type: 'string',
			label: '',
			value: null,
			queryName: 'ppc'
		},
		{
			id: 'pvt-tpc',
			type: 'string',
			label: '',
			value: null,
			queryName: 'tpc'
		},
		{
			id: 'pvt-g',
			type: 'string',
			label: '',
			value: null,
			queryName: 'g'
		},
	],
	chartsData: [],
	downloadLink: "",
	chartConfig: {},
	requiredSubscriptionLevel: "free",
};

const pvtCalculatorSlice = createSlice({
	name: "pvtCalculator",
	initialState,
	selectors: {
		getDataForIPR: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();

			const pvtInputs: IDisplayField[] = [];
			state.inputs.forEach(input => {
				pvtInputs.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `pvt:form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			pvtInputs.push(
				{
					id: "pvt-criteria",
					queryName: "criteria",
					displayValue: state.criteria.selected,
					translationKey: `pvt:criteria_title`,
					currentValue: state.criteria.selected,
					baseValue: state.criteria.selected,
					unitCategoryId: null,
					baseUnitId: null,
					currentUnitId: null,
					type: null,
					label: "",
					userValue: null,
					userUnitId: null
				}
			);

			let errors: string[] = [];

			if (state.strategy.selected === "gascompose") {
				validateGasCompositionInputs();
				if (state.gasCompositionError) {
					errors.push("gas_composition_sum_common");
				}
				state.gasCompositionInputs.forEach(input => {
					pvtInputs.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			} else if (state.strategy.selected === "g") {
				validateGInputs();
				state.gInputs.forEach(input => {
					pvtInputs.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			}

			return { inputs: pvtInputs, errors: errors };
		},
		getDataForVLP: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();

			const vlpInputs: IDisplayField[] = [];

			vlpInputs.push({
				id: "pvt-criteria",
				queryName: "criteria",
				displayValue: state.criteria.selected,
				translationKey: `pvt:criteria_title`,
				currentValue: state.criteria.selected,
				baseValue: state.criteria.selected,
				unitCategoryId: null,
				baseUnitId: null,
				currentUnitId: null,
				type: null,
				label: "",
				userValue: null,
				userUnitId: null
			});

			state.inputs.forEach(input => {
				vlpInputs.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `pvt:form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			let errors: string[] = [];

			if (state.strategy.selected === "gascompose") {
				validateGasCompositionInputs();
				if (state.gasCompositionError) {
					errors.push("gas_composition_sum_common");
				}
				state.gasCompositionInputs.forEach(input => {
					vlpInputs.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			} else if (state.strategy.selected === "g") {
				validateGInputs();
				state.gInputs.forEach(input => {
					vlpInputs.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			}

			return { inputs: vlpInputs, errors: errors };
		},
		getDataForSystemPlot: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();
			validateGInputs();
			const requiredInputs = ["temperature", "pressure", "criteria", "gas_density", "x_co2", "x_h2s", "x_n2"];

			const inputs: IDisplayField[] = [];

			inputs.push({
				id: "pvt-criteria",
				queryName: "criteria",
				displayValue: state.criteria.selected,
				translationKey: `pvt:criteria_title`,
				currentValue: state.criteria.selected,
				baseValue: state.criteria.selected,
				unitCategoryId: null,
				baseUnitId: null,
				currentUnitId: null,
				type: null,
				label: "",
				userValue: null,
				userUnitId: null
			});

			[...state.inputs, ...state.gInputs].filter(input => requiredInputs.includes(input.queryName)).forEach(input => {
				inputs.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `pvt:form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			let errors: string[] = [];
			if (inputs.some(input => requiredInputs.includes(input.queryName) && (input.currentValue === null || input.currentValue === undefined))) {
				errors.push("missing_required_inputs");
			}

			console.log(inputs, errors);

			return { inputs: inputs, errors: errors };
		},
		getDataForGRC: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();
			validateGInputs();
			const errors = [];

			const requareInputs = ["temperature", "pressure", "gas_density", "x_co2", "x_h2s", "x_n2"];
			const inputsForGasRC: IDisplayField[] = [];

			state.inputs.filter(input => requareInputs.includes(input.queryName)).forEach(input => {
				inputsForGasRC.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			inputsForGasRC.push(
				{
					id: "pvt-criteria",
					queryName: "criteria",
					baseValue: state.criteria.selected,
					displayValue: state.criteria.selected,
					translationKey: `pvt:criteria_title`,
					currentValue: null,
					type: null,
					unitCategoryId: null,
					baseUnitId: null,
					currentUnitId: null,
					label: "",
					userValue: null,
					userUnitId: null,
				}
			);

			if (state.strategy.selected == "g") {
				state.gInputs.forEach(input => {
					inputsForGasRC.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			}
			else {
				errors.push("invalid_strategy_selected");
			}

			return { inputs: inputsForGasRC, errors: errors };
		},
		getDataForPressureDrop: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();
			validateGInputs();
			const errors = [];

			const requareInputs = ["temperature", "pressure", "criteria", "gas_density", "x_co2", "x_h2s", "x_n2"];
			const inputsForGasRC: IDisplayField[] = [];

			state.inputs.filter(input => requareInputs.includes(input.queryName)).forEach(input => {
				inputsForGasRC.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			inputsForGasRC.push(
				{
					id: "pvt-criteria",
					queryName: "criteria",
					baseValue: state.criteria.selected,
					displayValue: state.criteria.selected,
					translationKey: `pvt:criteria_title`,
					currentValue: null,
					type: null,
					unitCategoryId: null,
					baseUnitId: null,
					currentUnitId: null,
					label: "",
					userValue: null,
					userUnitId: null,
				}
			);

			if (state.strategy.selected == "g") {
				state.gInputs.forEach(input => {
					inputsForGasRC.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			}
			else {
				errors.push("invalid_strategy_selected");
			}

			return { inputs: inputsForGasRC, errors: errors };
		},
		getDataForMaterialBalance: (state): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();
			validateGInputs();
			const errors = [];

			const requareInputs = ["temperature", "pressure", "criteria", "gas_density", "x_co2", "x_h2s", "x_n2"];
			const inputsForGasRC: IDisplayField[] = [];

			state.inputs.filter(input => requareInputs.includes(input.queryName)).forEach(input => {
				inputsForGasRC.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `form_fields.${input.id}`,
						error: input && input.currentValue != null ? input.error : "empty",
					}
				);
			});

			inputsForGasRC.push(
				{
					id: "pvt-criteria",
					queryName: "criteria",
					baseValue: state.criteria.selected,
					displayValue: state.criteria.selected,
					translationKey: `pvt:criteria_title`,
					currentValue: null,
					type: null,
					unitCategoryId: null,
					baseUnitId: null,
					currentUnitId: null,
					label: "",
					userValue: null,
					userUnitId: null,
				}
			);

			if (state.strategy.selected == "g") {
				state.gInputs.forEach(input => {
					inputsForGasRC.push(
						{
							...input,
							displayValue: input.currentValue,
							translationKey: `form_fields.${input.id}`,
							error: input && input.currentValue != null ? input.error : "empty",
						}
					);
				});
			}
			else {
				errors.push("invalid_strategy_selected");
			}

			return { inputs: inputsForGasRC, errors: errors };
		},
	},
	reducers: {
		setInputsData: (state, action: PayloadAction<IPVTCalculator>) => {
			state.inputs = action.payload.inputs;
			state.gInputs = action.payload.gInputs;
			state.gasCompositionInputs = action.payload.gasCompositionInputs;
		},
		setInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.inputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			})
			state.results.forEach(r => {
				r.value = null;
			})
			state.intermediateResults.forEach(r => {
				r.value = null;
			})
			state.chartsData = [];
		},
		setGInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.gInputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			})
			state.results.forEach(r => {
				r.value = null;
			})
			state.intermediateResults.forEach(r => {
				r.value = null;
			})
			state.chartsData = [];
		},
		setGasCompositionInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.gasCompositionInputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			})
			state.results.forEach(r => {
				r.value = null;
			})
			state.intermediateResults.forEach(r => {
				r.value = null;
			})
			state.chartsData = [];
		},
		updateInputByUnit: (state, action: PayloadAction<{ inputId: string, selectedUnitId: string | null }>) => {
			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					const unit = action.payload.selectedUnitId as unitId;
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, unit) : null;
					i.currentUnitId = unit;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);
					return;
				}
			})
		},
		setCriteria: (state, action: PayloadAction<PVTCriteriaOption>) => {
			state.criteria.selected = action.payload;

			state.results.forEach(r => {
				r.value = null;
			})
			state.intermediateResults.forEach(r => {
				r.value = null;
			})
			state.chartsData = [];
		},
		setStrategy: (state, action: PayloadAction<PVTStrategyOption>) => {
			state.strategy.selected = action.payload;

			state.results.forEach(r => {
				r.value = null;
			})
			state.intermediateResults.forEach(r => {
				r.value = null;
			})
			state.chartsData = [];
		},
		validateInputs: (state) => {
			state.inputs.forEach(input => {
				if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
		},
		validateGInputs: (state) => {
			state.gInputs.forEach(input => {
				if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
			//Max 5% N2,2 % CO2, 2% H2S
		},
		validateGasCompositionInputs: (state) => {
			state.gasCompositionInputs.forEach(input => {
				input.error = null;
			});

			const gasCompositionSum = state.gasCompositionInputs.reduce((acc, i) => acc + (i.currentValue ?? 0), 0);
			const isEqualOne = Math.abs(gasCompositionSum - 1) < 1e-9;
			state.gasCompositionError = isEqualOne ? null : "validation.gas_composition_sum";

			const requiredFields: string[] = ["pvt-field-c1", "pvt-field-c2", "pvt-field-c3", "pvt-field-nc4", "pvt-field-ic4"];
			state.gasCompositionInputs.forEach(input => {
				if (requiredFields.includes(input.id) && input.currentValue == null)
					input.error = "validation.is_required";
			});

			state.gasCompositionInputs.forEach(input => {
				if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				}
			});
		},

	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "pvt") {
					return;
				}
				const response = action.payload as IPVTRequestResult;
				if (response?.results) {
					state.results = state.results.map(r => ({
						...r,
						value: response.results[r.queryName]
					}));
					state.intermediateResults = state.intermediateResults.map(r => ({
						...r,
						value: response.results[r.queryName]
					}));
				}

				const plots = response?.plots["0"];
				const length = Object.keys(plots?.pressure || {}).length;

				if (length && length > 0) {
					const plotData = [];
					for (let i = 0; i < length; i++) {
						plotData.push({
							p: plots.pressure[i],
							z: plots.z[i],
							m: plots.m[i]
						});
					}
					state.chartsData = plotData;
				}
			})
	}
})

export default pvtCalculatorSlice

export const {
	setCriteria,
	setStrategy,
	validateInputs,
	validateGInputs,
	validateGasCompositionInputs,
	setInputValue,
	setGInputValue,
	setGasCompositionInputValue,
	setInputsData,
	updateInputByUnit
} = pvtCalculatorSlice.actions

export const {
	getDataForIPR,
	getDataForVLP,
	getDataForSystemPlot,
	getDataForGRC,
	getDataForPressureDrop,
	getDataForMaterialBalance
} = pvtCalculatorSlice.selectors;